import { useState, useEffect, useCallback } from "react";
import {
    HandHeart,
    Plus,
    Trash2,
    Search,
    Stethoscope,
} from "lucide-react";
import { Modal, Input, Checkbox, message, Spin } from "antd";
import { mixGetV1 } from "../services/mixin";

interface ServiceItem {
    id: number;
    name: string;
    price: number;
    qty: number;
    category?: string;
}

// Map dịch vụ từ chi tiết lịch hẹn (appointment.services) -> item hiển thị
function mapApiServices(apiServices?: any[]): ServiceItem[] {
    if (!Array.isArray(apiServices)) return [];
    return apiServices.map((s: any) => ({
        id: s?.pivot?.service_id ?? s?.id,
        name: s?.name ?? "",
        price: Number(s?.pivot?.price ?? s?.price ?? 0) || 0,
        qty: Number(s?.pivot?.quantity ?? 1) || 1,
        category: s?.category?.name || undefined,
    }));
}

interface Props {
    services?: any[];
    // Lưu danh sách dịch vụ (thêm/xóa) vào lịch hẹn; trả về true nếu thành công
    onSaveServiceIds?: (serviceIds: number[]) => Promise<boolean>;
    /** Không có quyền sửa thì chỉ xem: ẩn nút thêm và nút xóa dịch vụ */
    canEdit?: boolean;
}

export default function CustomerServiceInfo({ services: apiServices, onSaveServiceIds, canEdit = true }: Props) {
    const [openServiceModal, setOpenServiceModal] = useState(false);
    const [services, setServices] = useState<ServiceItem[]>(() => mapApiServices(apiServices));
    const [saving, setSaving] = useState(false);

    // Nạp lại dịch vụ thật khi dữ liệu lịch hẹn thay đổi
    useEffect(() => {
        setServices(mapApiServices(apiServices));
    }, [apiServices]);

    const formatCurrency = (value: number) =>
        value.toLocaleString("vi-VN") + " ₫";

    const totalPrice = services.reduce(
        (sum, item) => sum + item.price * item.qty,
        0
    );

    // Xóa 1 dịch vụ -> lưu danh sách còn lại
    const removeService = async (id: number) => {
        if (!onSaveServiceIds || saving) return;
        const remaining = services.filter((item) => item.id !== id).map((item) => item.id);
        setSaving(true);
        await onSaveServiceIds(remaining);
        setSaving(false);
    };

    // ----- Modal thêm dịch vụ (load dịch vụ thật) -----
    const [modalServices, setModalServices] = useState<{ id: number; name: string; price: number; category?: string }[]>([]);
    const [loadingModal, setLoadingModal] = useState(false);
    const [searchKw, setSearchKw] = useState("");
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

    const fetchModalServices = useCallback(async (search?: string) => {
        setLoadingModal(true);
        try {
            const params: any = { take: 50, status: 0 };
            if (search) params.search = search;
            const res = await mixGetV1("/api/v1/be/services", params);
            const items = res?.data?.items || (Array.isArray(res?.data) ? res.data : []);
            setModalServices(
                items.map((it: any) => ({
                    id: it.id,
                    name: it.name,
                    price: Number(it.price ?? 0) || 0,
                    category: it.category?.name || undefined,
                }))
            );
        } catch (e) {
            console.error("Failed to fetch services:", e);
        } finally {
            setLoadingModal(false);
        }
    }, []);

    // Mở modal: nạp dịch vụ + tick sẵn các dịch vụ đang có trong lịch hẹn
    const openModal = () => {
        setSelectedIds(new Set(services.map((s) => s.id)));
        setSearchKw("");
        setOpenServiceModal(true);
        fetchModalServices();
    };

    // Tìm kiếm dịch vụ trong modal
    useEffect(() => {
        if (!openServiceModal) return;
        const tid = setTimeout(() => fetchModalServices(searchKw || undefined), 400);
        return () => clearTimeout(tid);
    }, [searchKw, openServiceModal, fetchModalServices]);

    const toggleSelect = (id: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    // Áp dụng lựa chọn -> lưu vào lịch hẹn
    const applySelection = async () => {
        if (!onSaveServiceIds || saving) return;
        setSaving(true);
        const ok = await onSaveServiceIds([...selectedIds]);
        setSaving(false);
        if (ok) setOpenServiceModal(false);
    };

    return (
        <div className="bg-white dark:bg-[#1f1f1f] rounded-xl shadow-sm border border-slate-100 dark:border-[#2a2a2a] p-6 mb-6 text-000000e0">

            {/* Header */}
            <div className="
                flex
                flex-col
                gap-3
                md:flex-row
                md:items-center
                md:justify-between
                border-b
                border-slate-100
                dark:border-[#2a2a2a]
                pb-3.5
                mb-4
            ">
                <div className="title-info gap-2 text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center">
                    <HandHeart
                        size={16}
                        className="text-[#4e91ab]"
                    />
                    3. Thông tin dịch vụ khách đặt
                </div>

                {canEdit && (
                    <button
                        onClick={openModal}
                        disabled={saving}
                        className="
                        w-full
                        md:w-auto
                        inline-flex
                        items-center
                        justify-center
                        gap-1.5
                        px-4
                        py-2
                        bg-[#4e91ab]
                        hover:bg-[#316c83]
                        disabled:opacity-50
                        text-white
                        text-sm
                        font-bold
                        rounded-lg
                        transition
                    "
                    >
                        <Plus size={14} />
                        Thêm dịch vụ
                    </button>
                )}
            </div>

            {/* Table */}
            <div className="w-full !overflow-x-auto relative">
                {saving && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-black/30">
                        <Spin size="small" />
                    </div>
                )}
                <table className="md:min-w-[700px] text-left w-full">
                    <thead>
                        <tr className="border-b border-slate-100 dark:border-[#2a2a2a] text-xs font-bold text-slate-400 uppercase tracking-wider">
                            <th className="pb-3">Dịch vụ</th>
                            <th className="pb-3 text-right w-[140px]">
                                Thành tiền
                            </th>
                            {canEdit && (
                                <th className="pb-3 text-center w-[70px]">
                                    Xóa
                                </th>
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        {services.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={canEdit ? 3 : 2}
                                    className="py-10 text-center text-slate-400"
                                >
                                    Chưa có dịch vụ nào
                                </td>
                            </tr>
                        ) : (
                            services.map((srv) => (
                                <tr
                                    key={srv.id}
                                    className="border-b border-slate-100 dark:border-[#2a2a2a]"
                                >
                                    {/* Service */}
                                    <td className="py-4">
                                        <div className="font-semibold text-[15px] text-slate-800 dark:text-white">
                                            {srv.name}
                                        </div>

                                        {srv.category && (
                                            <span
                                                className="
                                                    inline-block
                                                    mt-1.5
                                                    px-2
                                                    py-0.5
                                                    bg-slate-100
                                                    dark:bg-[#2a2a2a]
                                                    rounded
                                                    text-[11px]
                                                    font-bold
                                                    text-slate-500
                                                "
                                            >
                                                {srv.category}
                                            </span>
                                        )}
                                    </td>

                                    {/* Total */}
                                    <td className="text-right font-bold">
                                        {formatCurrency(
                                            srv.price * srv.qty
                                        )}
                                    </td>

                                    {/* Delete */}
                                    {canEdit && (
                                        <td className="text-center">
                                            <button
                                                onClick={() => removeService(srv.id)}
                                                disabled={saving}
                                                className="
                                                p-2
                                                rounded-md
                                                text-slate-400
                                                hover:text-red-500
                                                hover:bg-red-50
                                                disabled:opacity-50
                                            "
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Footer */}
            <div
                className="
                    mt-4
                    pt-4
                    border-t-2
                    border-dashed
                    border-slate-100
                    dark:border-[#2a2a2a]
                    flex
                    flex-col
                    gap-3
                    md:flex-row
                    md:justify-between
                    md:items-center
                "
            >
                <div className="text-[11px] font-bold text-slate-400">
                    * Vui lòng kiểm tra kỹ danh sách dịch vụ trước khi thanh toán
                </div>

                <div className="flex items-center gap-6">
                    <span className="text-xs font-bold uppercase text-slate-400">
                        Tổng đơn hàng:
                    </span>

                    <span className="text-xl font-black text-[#4e91ab]">
                        {formatCurrency(totalPrice)}
                    </span>
                </div>
            </div>

            <Modal
                open={openServiceModal}
                onCancel={() => setOpenServiceModal(false)}
                footer={null}
                width={window.innerWidth < 768 ? "95%" : 900}
                centered
                styles={{
                    body: {
                        maxHeight: "70vh",
                        overflow: "auto",
                    },
                }}
            >
                <div className="space-y-4">

                    {/* Header */}
                    <div className="title-info gap-2 text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center">
                        <Stethoscope size={16} className="text-[#4e91ab]" />
                        DANH SÁCH DỊCH VỤ Y KHOA & SPA
                    </div>

                    {/* Search */}
                    <Input
                        prefix={<Search size={16} />}
                        placeholder="Tìm kiếm liệu trình hoặc dịch vụ..."
                        value={searchKw}
                        onChange={(e) => setSearchKw(e.target.value)}
                        allowClear
                    />

                    {/* List */}
                    <div className="max-h-[400px] overflow-y-auto space-y-3">
                        {loadingModal ? (
                            <div className="py-10 flex justify-center">
                                <Spin />
                            </div>
                        ) : modalServices.length === 0 ? (
                            <div className="py-10 text-center text-slate-400">Không tìm thấy dịch vụ</div>
                        ) : (
                            modalServices.map((item) => (
                                <div
                                    key={item.id}
                                    className="
                                        border
                                        rounded-xl
                                        p-4
                                        flex
                                        items-start
                                        gap-3
                                        hover:border-[#3e91ab]
                                        cursor-pointer
                                    "
                                    onClick={() => toggleSelect(item.id)}
                                >
                                    <Checkbox
                                        checked={selectedIds.has(item.id)}
                                        onChange={() => toggleSelect(item.id)}
                                        onClick={(e) => e.stopPropagation()}
                                    />

                                    <div>
                                        <div className="text-sm font-bold text-slate-800 text-000000e0">
                                            {item.name}
                                        </div>
                                        {item.category && (
                                            <div className="text-[11px] text-slate-400 mt-0.5">
                                                {item.category}
                                            </div>
                                        )}
                                        <div className="text-xs text-slate-400 font-bold mt-0.5">
                                            {formatCurrency(item.price)}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex justify-between items-center border-t pt-4">

                        <div className="text-sm text-gray-500">
                            Đã chọn: {selectedIds.size} dịch vụ
                        </div>

                        <div className="flex gap-2 w-full md:w-auto">
                            <button
                                onClick={() => setOpenServiceModal(false)}
                                disabled={saving}
                                className="
                                    flex-1
                                    md:flex-none
                                    px-5
                                    py-2
                                    disabled:opacity-50
                                "
                            >
                                Đóng
                            </button>

                            <button
                                onClick={applySelection}
                                disabled={saving}
                                className="
                                    px-5 py-2
                                    bg-[#4a93ab]
                                    text-white
                                    rounded-lg
                                    disabled:opacity-50
                                "
                            >
                                {saving ? "Đang lưu..." : "Áp dụng chọn"}
                            </button>
                        </div>

                    </div>

                </div>
            </Modal>
        </div>
    );
}
